import { useMutation, useQuery } from '@apollo/client';
import { PlusIcon, RefreshIcon, XIcon } from '@heroicons/react/outline';
import { Button } from 'components/Button';
import { MultiSelect } from 'components/MultiSelect';
import { Toggle } from 'components/Toggle';
import { SYNC_SHOPIFY_ATTRIBUTES } from 'gql/mutations';
import { GET_RETURN_PRODUCT_ELIGIBILITY_CATALOG } from 'gql/queries';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SizeEnum } from 'utils';
import { DatedProductRule } from 'types/policy';
import { DatedProductRulesEditor } from './DatedProductRulesEditor';
import {
  getProductEligibilityActivationState,
  getProductEligibilityCatalogState,
  ProductEligibilityCapability,
  ProductEligibilityCollection,
  splitProductExclusionValues,
} from './PolicySettings.productEligibility';

type ProductEligibilityTag = {
  id: string;
  label: string;
};

type ProductEligibilityCatalog = {
  capability: ProductEligibilityCapability;
  tags: ProductEligibilityTag[];
  collections: ProductEligibilityCollection[];
  finalSaleMetafield?: {
    namespace: string;
    key: string;
    type: string;
  } | null;
  syncedAt?: string | null;
  stale: boolean;
  error?: string | null;
};

const MAX_EXCLUDED_SKUS = 250;
const MAX_SKU_LENGTH = 255;

export type ProductEligibilityActivationState = {
  storeId?: string;
  ready: boolean;
  reason: string | null;
};

type ProductEligibilityControlsProps = {
  storeId?: string;
  productRulesEnabled: boolean;
  activationPending: boolean;
  requiresReconfirmation: boolean;
  finalSaleEnforced: boolean;
  excludedTags: string[];
  excludedCollections: string[];
  excludedSkus: string[];
  datedRules?: DatedProductRule[];
  datedRulesLoadError?: string | null;
  disabled?: boolean;
  onDatedRulesChange?: (rules: DatedProductRule[]) => void;
  excludedCollectionLabels?: Record<string, string>;
  onProductRulesEnabledChange: (enabled: boolean) => void;
  onFinalSaleEnforcedChange: (enabled: boolean) => void;
  onExclusionsChange: (value: {
    excludedTags: string[];
    excludedCollections: string[];
    excludedSkus: string[];
    excludedCollectionLabels: Record<string, string>;
  }) => void;
  onActivationStateChange: (state: ProductEligibilityActivationState) => void;
};

const formatCatalogSyncTime = (value: Date | null) => {
  if (!value) return null;
  return value.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const ProductEligibilityControls = ({
  storeId,
  productRulesEnabled,
  activationPending,
  finalSaleEnforced,
  excludedTags,
  excludedCollections,
  excludedSkus,
  datedRules = [],
  datedRulesLoadError,
  disabled,
  onDatedRulesChange,
  excludedCollectionLabels,
  onProductRulesEnabledChange,
  onFinalSaleEnforcedChange,
  onExclusionsChange,
  onActivationStateChange,
}: ProductEligibilityControlsProps) => {
  const [syncError, setSyncError] = useState<string | null>(null);
  const [catalogRefreshing, setCatalogRefreshing] = useState(false);
  const [skuInput, setSkuInput] = useState('');
  const syncGeneration = useRef(0);
  const {
    data,
    loading: catalogLoading,
    error: catalogQueryError,
    refetch,
  } = useQuery(GET_RETURN_PRODUCT_ELIGIBILITY_CATALOG, {
    variables: { input: { storeId } },
    skip: !storeId,
    fetchPolicy: 'network-only',
  });
  const [syncShopifyAttributes, { loading: catalogSyncing }] = useMutation(
    SYNC_SHOPIFY_ATTRIBUTES,
  );

  useEffect(() => {
    syncGeneration.current += 1;
    setCatalogRefreshing(false);
    setSyncError(null);
    setSkuInput('');

    return () => {
      syncGeneration.current += 1;
    };
  }, [storeId]);

  const catalog = data?.returnProductEligibilityCatalog as
    | ProductEligibilityCatalog
    | undefined;
  const capability = catalog?.capability;
  const catalogState = getProductEligibilityCatalogState(
    capability
      ? {
          ...capability,
          catalogSyncedAt:
            catalog?.syncedAt || capability.catalogSyncedAt || null,
        }
      : undefined,
    new Date(),
    Boolean(catalog?.stale),
  );
  const requestError = syncError || catalogQueryError?.message;
  const catalogError = requestError || catalog?.error;
  const shopifyCatalogPermissionMissing = Boolean(
    !capability?.scopesReady &&
      catalog?.error?.includes(
        'Shopify product catalog permission is unavailable',
      ),
  );
  const catalogReady = Boolean(
    !catalogLoading &&
      !catalogRefreshing &&
      !catalogError &&
      catalogState.ready,
  );
  const collections = useMemo(
    () => catalog?.collections || [],
    [catalog?.collections],
  );
  const tags = useMemo(() => catalog?.tags || [], [catalog?.tags]);
  const knownCollectionIds = useMemo(
    () => collections.map(({ id }) => id),
    [collections],
  );
  const knownTagIds = useMemo(() => tags.map(({ id }) => id), [tags]);
  const activation = getProductEligibilityActivationState({
    catalogReady,
    finalSaleEnforced,
    finalSaleDefinitionReady: Boolean(capability?.finalSaleDefinitionReady),
    excludedTags,
    excludedCollections,
    excludedSkus,
    datedRules,
    knownCollectionIds,
    knownTagIds,
  });

  useEffect(() => {
    onActivationStateChange({
      storeId,
      ready: activation.ready && !datedRulesLoadError,
      reason: datedRulesLoadError || activation.reason,
    });
  }, [
    activation.ready,
    activation.reason,
    datedRulesLoadError,
    onActivationStateChange,
    storeId,
  ]);

  const selectedExclusions = useMemo(
    () => [
      ...excludedCollections.map((id) => `collection:${id}`),
      ...excludedTags.map((tag) => `tag:${tag}`),
    ],
    [excludedCollections, excludedTags],
  );

  const exclusionOptions = useMemo(() => {
    const knownCollections = new Set(knownCollectionIds);
    const knownTags = new Set(knownTagIds);
    const legacyCollections = excludedCollections
      .filter((id) => !knownCollections.has(id))
      .map((id) => ({
        label: `Legacy collection: ${excludedCollectionLabels?.[id] || id}`,
        val: `collection:${id}`,
      }));
    const savedMissingTags = excludedTags
      .filter((tag) => !knownTags.has(tag))
      .map((tag) => ({
        label: `Saved tag: ${tag}`,
        val: `tag:${tag}`,
      }));

    return [
      ...(collections.length
        ? [
            {
              label: 'Collections',
              val: 'heading:collections',
              isHeader: true,
            },
          ]
        : []),
      ...collections.map((collection) => ({
        label: `Collection: ${collection.title}`,
        val: `collection:${collection.id}`,
      })),
      ...legacyCollections,
      ...(tags.length
        ? [{ label: 'Tags', val: 'heading:tags', isHeader: true }]
        : []),
      ...tags.map((tag) => ({
        label: `Tag: ${tag.label}`,
        val: `tag:${tag.id}`,
      })),
      ...savedMissingTags,
    ];
  }, [
    collections,
    excludedCollectionLabels,
    excludedCollections,
    excludedTags,
    knownCollectionIds,
    knownTagIds,
    tags,
  ]);

  const handleExclusionsChange = (values: Array<number | string>) => {
    const next = splitProductExclusionValues(values, collections);
    const selectedLegacyCollections = values
      .filter(
        (value): value is string =>
          typeof value === 'string' && value.startsWith('collection:'),
      )
      .map((value) => value.slice('collection:'.length))
      .filter((id) => !knownCollectionIds.includes(id));

    selectedLegacyCollections.forEach((id) => {
      next.excludedCollections.push(id);
      next.excludedCollectionLabels[id] = excludedCollectionLabels?.[id] || id;
    });
    onExclusionsChange({ ...next, excludedSkus });
  };

  const normalizedSkuInput = skuInput.trim().toLocaleLowerCase('en-US');
  const skuLimitReached = excludedSkus.length >= MAX_EXCLUDED_SKUS;
  const skuTooLong = skuInput.trim().length > MAX_SKU_LENGTH;
  const skuAlreadyExists = Boolean(
    normalizedSkuInput &&
      excludedSkus.some(
        (sku) => sku.trim().toLocaleLowerCase('en-US') === normalizedSkuInput,
      ),
  );

  const handleAddSku = () => {
    const nextSku = skuInput.trim();
    if (!nextSku || skuAlreadyExists || skuTooLong || skuLimitReached) return;

    onExclusionsChange({
      excludedTags,
      excludedCollections,
      excludedSkus: [...excludedSkus, nextSku],
      excludedCollectionLabels: excludedCollectionLabels || {},
    });
    setSkuInput('');
  };

  const handleRemoveSku = (indexToRemove: number) => {
    onExclusionsChange({
      excludedTags,
      excludedCollections,
      excludedSkus: excludedSkus.filter((_, index) => index !== indexToRemove),
      excludedCollectionLabels: excludedCollectionLabels || {},
    });
  };

  const handleSyncCatalog = async () => {
    if (!storeId || catalogRefreshing || catalogSyncing) return;

    syncGeneration.current += 1;
    const generation = syncGeneration.current;
    setSyncError(null);
    setCatalogRefreshing(true);

    try {
      await syncShopifyAttributes({ variables: { storeId } });
      if (syncGeneration.current !== generation) return;
      await refetch();
    } catch (error) {
      if (syncGeneration.current !== generation) return;
      setSyncError('Shopify catalog sync failed. Please try again.');
    } finally {
      if (syncGeneration.current === generation) {
        setCatalogRefreshing(false);
      }
    }
  };

  const status = (() => {
    if (catalogLoading)
      return { label: 'Loading', className: 'bg-gray-100 text-gray-600' };
    if (requestError)
      return { label: 'Error', className: 'bg-red-100 text-red-700' };
    if (!capability?.available)
      return { label: 'Unavailable', className: 'bg-gray-100 text-gray-600' };
    if (shopifyCatalogPermissionMissing)
      return {
        label: 'Permissions required',
        className: 'bg-amber-100 text-amber-800',
      };
    if (catalog?.error)
      return { label: 'Error', className: 'bg-red-100 text-red-700' };
    if (!capability.scopesReady)
      return {
        label: 'Sync required',
        className: 'bg-amber-100 text-amber-800',
      };
    if (!capability.apiVersionReady)
      return {
        label: 'Shopify update required',
        className: 'bg-amber-100 text-amber-800',
      };
    if (catalogState.stale)
      return {
        label: 'Sync required',
        className: 'bg-amber-100 text-amber-800',
      };
    if (activationPending)
      return {
        label: 'Pending save',
        className: 'bg-amber-100 text-amber-800',
      };
    return productRulesEnabled
      ? { label: 'Active', className: 'bg-green-100 text-green-800' }
      : { label: 'Ready', className: 'bg-blue-100 text-blue-800' };
  })();

  const syncedLabel = formatCatalogSyncTime(catalogState.syncedAt);
  const finalSaleDefinitionReady = Boolean(
    catalogReady && capability?.finalSaleDefinitionReady,
  );

  return (
    <div className="space-y-6 p-5">
      <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-gray-900">
              Shopify product catalog
            </p>
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${status.className}`}
            >
              {status.label}
            </span>
          </div>
          {catalogLoading ? (
            <p className="mt-2 text-xs text-gray-600">
              Loading Shopify catalog…
            </p>
          ) : requestError ? (
            <p className="mt-2 text-xs text-red-700" role="alert">
              Shopify catalog could not be loaded. {requestError}
            </p>
          ) : !capability?.available ? (
            <p className="mt-2 text-xs text-gray-600">
              Shopify product exclusions are unavailable for this store.
              {catalog?.error ? ` ${catalog.error}` : ''}
            </p>
          ) : shopifyCatalogPermissionMissing ? (
            <p className="mt-2 text-xs text-amber-800">
              Reconnect Shopify to grant the product catalog permissions.
            </p>
          ) : catalog?.error ? (
            <p className="mt-2 text-xs text-red-700" role="alert">
              Shopify catalog could not be loaded. {catalog.error}
            </p>
          ) : !capability.scopesReady ? (
            <p className="mt-2 text-xs text-amber-800">
              Sync the catalog to verify Shopify product permissions.
            </p>
          ) : !capability.apiVersionReady ? (
            <p className="mt-2 text-xs text-amber-800">
              Update this store&apos;s Shopify API connection before activating.
            </p>
          ) : (
            <p className="mt-2 text-xs text-gray-600">
              {syncedLabel
                ? `Catalog synced ${syncedLabel}${
                    catalogState.stale ? ' · sync required' : ''
                  }.`
                : 'Sync the catalog before choosing product tags or collections.'}
            </p>
          )}
        </div>
        <Button
          size={SizeEnum.sm}
          variant="outlined"
          onClick={handleSyncCatalog}
          isLoading={catalogSyncing || catalogRefreshing}
          disabled={
            !storeId ||
            catalogLoading ||
            catalogRefreshing ||
            capability?.available === false
          }
          className="flex-shrink-0 justify-center"
        >
          <RefreshIcon className="mr-1.5 h-4 w-4" />
          Sync catalog
        </Button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white px-4 py-4">
        <Toggle
          isEnabled={productRulesEnabled}
          onChange={onProductRulesEnabledChange}
          disabled={
            disabled ||
            (!productRulesEnabled &&
              (!activation.ready || Boolean(datedRulesLoadError)))
          }
          rightLabel="Apply product exclusions"
          rightDescription="Turn this on only after reviewing the exclusions. Saved exclusions apply to new return and exchange decisions."
          ariaLabel="Apply product exclusions"
        />
        {!activation.ready && activation.reason ? (
          <p className="mt-3 text-xs leading-5 text-amber-800">
            {activation.reason}
          </p>
        ) : null}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white px-4 py-4">
        <Toggle
          isEnabled={finalSaleEnforced}
          onChange={onFinalSaleEnforcedChange}
          disabled={!finalSaleEnforced && !finalSaleDefinitionReady}
          rightLabel="Final sale enforcement"
          rightDescription={
            finalSaleDefinitionReady
              ? 'Uses the merchant-owned shipaid.final_sale boolean metafield. A variant value overrides its product value.'
              : 'Create the shipaid.final_sale boolean metafield before enabling this rule.'
          }
          ariaLabel="Final sale enforcement"
        />
      </div>

      <div>
        <MultiSelect
          id="return-exclusions"
          label="Product tags and collections"
          options={exclusionOptions}
          selected={selectedExclusions}
          setSelected={handleExclusionsChange}
          placeholder="Select tags or collections to exclude…"
          emptyMessage="No Shopify tags or collections found. Sync the catalog to refresh it."
          disabled={!catalogReady}
        />
        <p className="mt-2 text-xs leading-5 text-gray-500">
          Collections are stored by stable Shopify ID. Labels are saved only so
          your team can recognize each rule if a collection is later renamed.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white px-4 py-4">
        <div className="grid gap-4 md:grid-cols-2 md:items-start">
          <div>
            <label
              htmlFor="return-excluded-sku"
              className="block text-sm font-medium text-gray-700"
            >
              Exclude by SKU
            </label>
            <p
              id="return-excluded-sku-description"
              className="mt-1 text-xs leading-5 text-gray-500"
            >
              Enter exact product SKUs that should not be eligible for return.
              Matching is case-insensitive.
            </p>
          </div>
          <div className="flex gap-2">
            <input
              id="return-excluded-sku"
              type="text"
              value={skuInput}
              maxLength={MAX_SKU_LENGTH}
              disabled={skuLimitReached}
              aria-describedby={`return-excluded-sku-description${
                skuTooLong || skuLimitReached
                  ? ' return-excluded-sku-validation'
                  : ''
              }`}
              placeholder="Enter SKU"
              onChange={(event) => setSkuInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleAddSku();
                }
              }}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <button
              type="button"
              onClick={handleAddSku}
              disabled={
                !skuInput.trim() ||
                skuAlreadyExists ||
                skuTooLong ||
                skuLimitReached
              }
              className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-md bg-blue-700 px-3 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              <PlusIcon className="h-4 w-4" aria-hidden="true" />
              Add SKU
            </button>
          </div>
        </div>

        {skuAlreadyExists ? (
          <p className="mt-3 text-xs font-medium text-amber-700" role="status">
            {skuInput.trim()} already exists.
          </p>
        ) : null}

        {skuTooLong || skuLimitReached ? (
          <p
            id="return-excluded-sku-validation"
            className="mt-3 text-xs font-medium text-amber-700"
            role="status"
          >
            {skuLimitReached
              ? `You can add up to ${MAX_EXCLUDED_SKUS} SKU exclusions.`
              : `SKU must be ${MAX_SKU_LENGTH} characters or fewer.`}
          </p>
        ) : null}

        {excludedSkus.length > 0 ? (
          <div className="mt-4 flex flex-wrap gap-2">
            {excludedSkus.map((sku, index) => (
              <span
                key={`${sku}-${index}`}
                className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-gray-800"
              >
                {sku}
                <button
                  type="button"
                  aria-label={`Remove SKU ${sku}`}
                  onClick={() => handleRemoveSku(index)}
                  className="text-gray-500 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                >
                  <XIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="mt-4 rounded-md border border-dashed border-gray-200 bg-gray-50 p-3 text-center text-xs italic text-gray-500">
            No SKU exclusions added.
          </p>
        )}
      </div>

      {onDatedRulesChange && (
        <DatedProductRulesEditor
          key={storeId}
          rules={datedRules}
          tags={tags}
          catalogReady={catalogReady}
          disabled={disabled}
          loadError={datedRulesLoadError}
          onChange={onDatedRulesChange}
        />
      )}
    </div>
  );
};

export default ProductEligibilityControls;
